import type { GapDraftView } from '@momentum/types';

/**
 * What a draft is called, from whichever of three sources has a heading.
 *
 * `gap.title` is the product's own, written by `ENRICHMENT` or by a
 * reviewer. `source.title` is the finder's — a real authored heading that the
 * run has carried since the orientation fields landed. Only when neither exists
 * is one derived from the claim: its first sentence, clamped.
 *
 * The derivation is ported from the approved mockup (`mockup-d.js`
 * `deriveTitle`) so the queue and the reader agree on what a draft is called.
 */

/**
 * Sentence-enders that are not sentence ends. Without this guard a claim like
 * "no PO exists, e.g. for the Q3 batch" would be cut at "e.g.".
 */
const ABBREVIATIONS = [
  'e.g.',
  'i.e.',
  'etc.',
  'vs.',
  'approx.',
  'no.',
  'inc.',
  'ltd.',
  'dr.',
  'mr.',
  'ms.',
  'mrs.',
];

const SENTENCE_ENDERS = '.!?';

export function deriveTitle(claim: string, maxLength = 96): string {
  const text = claim.trim();
  let sentence = text;

  for (let index = 0; index < text.length - 1; index += 1) {
    if (!SENTENCE_ENDERS.includes(text[index] ?? '')) continue;
    if (!/\s/.test(text[index + 1] ?? ' ')) continue;

    const trailingWord = text
      .slice(0, index + 1)
      .split(/\s/)
      .pop()
      ?.toLowerCase();
    if (trailingWord && ABBREVIATIONS.includes(trailingWord)) continue;

    sentence = text.slice(0, index + 1);
    break;
  }

  sentence = sentence.replace(/\.$/, '');
  if (sentence.length <= maxLength) return sentence;

  // Clamp on a word boundary, unless that would leave a stub — then cut hard.
  const clamped = sentence.slice(0, maxLength);
  const lastSpace = clamped.lastIndexOf(' ');
  return `${clamped.slice(0, lastSpace > 40 ? lastSpace : maxLength)}…`;
}

/** Which of the three sources {@link titleOf} took the heading from. */
export type TitleOrigin = 'gap' | 'run' | 'derived';

/**
 * The product layer wins over the run, as it does for every other field — see
 * `product-value.ts`. What is different here is the middle step: a heading is
 * one of the few things the run authors outright, so falling through to a
 * sentence sliced off the claim when the finder wrote a real title would be
 * throwing away the better of the two.
 */
export function titleOrigin(draft: GapDraftView): TitleOrigin {
  if (draft.gap.title !== undefined) return 'gap';
  if (draft.source.title !== undefined) return 'run';
  return 'derived';
}

export function titleOf(draft: GapDraftView): string {
  return (
    draft.gap.title ?? draft.source.title ?? deriveTitle(draft.source.claim)
  );
}
